import React from 'react';
import PropTypes from 'prop-types';
import parse from 'html-react-parser';
import { parseCssString } from '../../utils/utils';

const HowItWorksCard = ({
  stepNumber,
  icon,
  title,
  description,
  isHighlighted,
  linkUrl,
  linkText,
  backgroundColor,
  imageStyles,
}) => {
  const cardClasses = isHighlighted
    ? 'text-white rounded-2xl p-6 flex flex-col h-full'
    : 'rounded-2xl shadow-sm border border-gray-100 p-6 flex flex-col h-full';

  const descriptionClasses = isHighlighted
    ? 'text-[#18293A] text-base mt-1 leading-relaxed'
    : 'text-[#707070] text-base mt-1 leading-relaxed';

  const bgStyle = backgroundColor
    ? { backgroundColor }
    : { backgroundColor: isHighlighted ? '#4CAF50' : '#FFFFFF' };

  return (
    <div className={cardClasses} style={bgStyle}>
      <div className="flex items-center justify-between">
        <div className="bg-[#1C2938] text-white w-7 h-7 rounded flex items-center justify-center text-xl font-normal">
          {stepNumber}
        </div>

        {isHighlighted && linkUrl && (
          <a
            href={linkUrl}
            className="inline-flex items-center text-[#18293A] font-semibold text-base hover:underline"
          >
            {linkText || 'Learn More'}
            <svg
              className="ml-2 w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 8l4 4m0 0l-4 4m4-4H3"
              />
            </svg>
          </a>
        )}
      </div>

      <div className="flex-grow flex flex-col pb-6">
        {icon && (
          <div className="mt-28 mb-2 min-h[80px] relative">
            <img
              src={icon}
              alt={title}
              style={parseCssString(imageStyles) || {}}
              className={`h-16 w-auto object-contain ${isHighlighted ? 'filter brightness-0 invert' : ''} absolute bottom-0 left-0`}
            />
          </div>
        )}

        <h3 className="text-[#18293A] font-semibold text-lg mt-4">{parse(title)}</h3>
        <p className={descriptionClasses}>{parse(description)}</p>
      </div>
    </div>
  );
};

HowItWorksCard.propTypes = {
  stepNumber: PropTypes.number.isRequired,
  icon: PropTypes.string,
  title: PropTypes.string.isRequired,
  imageStyles: PropTypes.string,
  description: PropTypes.string.isRequired,
  isHighlighted: PropTypes.bool,
  linkUrl: PropTypes.string,
  linkText: PropTypes.string,
  backgroundColor: PropTypes.string,
};

const SectionHowItWorks = ({ header, items, containerClasses, backgroundColor = '#F3F5F6' }) => {
  return (
    <div
      className={`py-14 lg:py-8 w-full ${containerClasses || ''}`}
      style={{ backgroundColor }}
    >
      {header && (
        <h2 className="text-3xl lg:text-4xl font-bold text-center text-[#1C2938] mb-10">
          {parse(header)}
        </h2>
      )}

      <div className="px-2 mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
          {items.map((item, index) => (
            <HowItWorksCard
              key={index}
              stepNumber={index + 1}
              icon={item.image?.url}
              title={item.header}
              description={item.body}
              isHighlighted={item.isHighlighted}
              linkUrl={item.linkUrl}
              linkText={item.linkText}
              backgroundColor={item.backgroundColor}
              imageStyles={item.imageStyles}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

SectionHowItWorks.propTypes = {
  header: PropTypes.string,
  containerClasses: PropTypes.string,
  backgroundColor: PropTypes.string,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      image: PropTypes.shape({
        url: PropTypes.string,
      }),
      imageStyles: PropTypes.string,
      header: PropTypes.string.isRequired,
      body: PropTypes.string.isRequired,
      isHighlighted: PropTypes.bool,
      linkUrl: PropTypes.string,
      linkText: PropTypes.string,
      backgroundColor: PropTypes.string,
    })
  ).isRequired,
};

export default SectionHowItWorks;
